import { activeTenant } from '../../lib/tenant'
import type {
  PublicAvailabilitySnapshot,
  PublicProject,
  PublicProjectUnit,
} from './types'
import type { SiteContent } from '../cms/siteContent'

const projectImages: Record<string, string> = {
  'Boca Chica Prestige I': '/assets/prestige-i.webp',
  'Boca Chica Prestige II': '/assets/prestige-ii.webp',
  'Boca Chica Residences': '/assets/residences.webp',
  'Boca Chica Residence': '/assets/residences.webp',
  'Boca Chica Towers': '/assets/towers.webp',
}

function fallbackProjectImage(projectName: string) {
  if (projectImages[projectName]) return projectImages[projectName]
  const normalized = projectName.toLowerCase()
  if (normalized.includes('tower')) return '/assets/towers.webp'
  if (normalized.includes('residence')) return '/assets/residences.webp'
  if (normalized.includes('prestige ii')) return '/assets/prestige-ii.webp'
  if (normalized.includes('prestige')) return '/assets/prestige-i.webp'
  return '/assets/towers.webp'
}

function projectPresentation(
  projectId: string,
  projectName: string,
  siteContent?: SiteContent,
) {
  const configured = siteContent?.projects.presentation?.[projectId]
  const gallery = configured?.galleryUrls ?? []
  const cover = configured?.coverImageUrl?.trim()
    || gallery.find(url => url.trim())?.trim()
    || fallbackProjectImage(projectName)

  return {
    cover,
    gallery: [...new Set([cover, ...gallery.map(url => url.trim()).filter(Boolean)])],
  }
}

function formatMoney(unit: PublicProjectUnit) {
  if (unit.price === null) return 'Consultar'
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: unit.currency,
    maximumFractionDigits: 0,
  }).format(unit.price)
}

function statusLabel(status: string) {
  if (status === 'available') return 'Disponible'
  if (status === 'held') return 'En proceso'
  if (status === 'reserved') return 'Reservada'
  return status
}

function formatCompactNumber(value: number) {
  return new Intl.NumberFormat('es-DO', { maximumFractionDigits: 2 }).format(value)
}

function renderUnitSpecs(unit: PublicProjectUnit) {
  const specs = document.createElement('div')
  specs.className = 'unit-specs'

  const items: Array<{ icon: string; value: string; label: string }> = []

  if (unit.bedrooms !== null) {
    items.push({
      icon: 'bed-double',
      value: formatCompactNumber(unit.bedrooms),
      label: unit.bedrooms === 1 ? 'habitación' : 'habitaciones',
    })
  }

  if (unit.bathrooms !== null) {
    items.push({
      icon: 'bath',
      value: formatCompactNumber(unit.bathrooms),
      label: unit.bathrooms === 1 ? 'baño' : 'baños',
    })
  }

  if (unit.parkingSpaces !== null) {
    items.push({
      icon: 'car-front',
      value: formatCompactNumber(unit.parkingSpaces),
      label: unit.parkingSpaces === 1 ? 'parqueo' : 'parqueos',
    })
  }

  if (unit.areaM2 !== null) {
    items.push({
      icon: 'ruler',
      value: `${formatCompactNumber(unit.areaM2)} m²`,
      label: 'área',
    })
  }

  items.forEach(item => {
    const element = document.createElement('div')
    element.className = 'unit-spec'
    element.setAttribute('aria-label', `${item.value} ${item.label}`)
    element.innerHTML = `<i data-lucide="${item.icon}" aria-hidden="true"></i><span><strong>${item.value}</strong><small>${item.label}</small></span>`
    specs.append(element)
  })

  return specs
}

function renderPublicCard(unit: PublicProjectUnit, siteContent?: SiteContent) {
  const article = document.createElement('article')
  article.className = 'unit-card'
  article.dataset.project = unit.projectName
  article.dataset.projectId = unit.projectId
  article.dataset.propertyType = unit.propertyType
  article.dataset.bedrooms = unit.bedrooms === null ? '' : String(unit.bedrooms)
  article.dataset.status = unit.status
  article.dataset.currency = unit.currency

  const image = document.createElement('img')
  image.src = projectPresentation(unit.projectId, unit.projectName, siteContent).cover
  image.alt = unit.projectName

  const body = document.createElement('div')
  body.className = 'unit-card__body'
  const badge = document.createElement('span')
  badge.className =
    unit.status === 'available'
      ? 'lozenge lozenge--success'
      : 'lozenge lozenge--warning'
  badge.textContent = statusLabel(unit.status)

  const title = document.createElement('h3')
  title.style.marginTop = '12px'
  title.textContent = unit.unitName || unit.unitCode
  const description = document.createElement('p')
  description.textContent = `${unit.projectName} · ${unit.unitCode}`

  const specs = renderUnitSpecs(unit)

  const meta = document.createElement('div')
  meta.className = 'unit-meta'
  meta.innerHTML = '<div><span>Tipo</span><strong></strong></div><div><span>Precio</span><strong></strong></div>'
  const values = meta.querySelectorAll('strong')
  values[0].textContent = unit.propertyType
  values[1].textContent = formatMoney(unit)

  const button = document.createElement('button')
  button.className = 'btn btn--primary btn--block'
  button.type = 'button'
  button.textContent = 'Seleccionar'
  button.disabled = unit.status !== 'available'
  button.addEventListener('click', () => {
    const selectUnit = (
      window as typeof window & {
        selectUnit?: (
          unit: string,
          project: string,
          price: string,
          unitId: string,
          projectId: string,
        ) => void
      }
    ).selectUnit
    selectUnit?.(
      unit.unitName || unit.unitCode,
      unit.projectName,
      formatMoney(unit),
      unit.id,
      unit.projectId,
    )
  })

  body.append(badge, title, description)
  if (specs.childElementCount) body.append(specs)
  body.append(meta, button)
  article.append(image, body)
  return article
}

function renderProjectCard(project: PublicProject, siteContent?: SiteContent) {
  const article = document.createElement('article')
  article.className = 'project-card'
  article.dataset.projectId = project.id

  const media = document.createElement('div')
  media.className = 'project-card__media'
  const image = document.createElement('img')
  const presentation = projectPresentation(project.id, project.name, siteContent)
  image.src = presentation.cover
  image.alt = `Render de ${project.name}`
  image.loading = 'lazy'
  const badge = document.createElement('span')
  badge.className = 'project-card__badge lozenge lozenge--success'
  badge.textContent = 'Publicado'
  media.append(image, badge)

  const body = document.createElement('div')
  body.className = 'project-card__body'
  const top = document.createElement('div')
  top.className = 'project-card__top'
  const copy = document.createElement('div')
  const title = document.createElement('h3')
  title.textContent = project.name
  const description = document.createElement('p')
  description.textContent =
    project.description || 'Proyecto disponible'
  copy.append(title, description)
  top.append(copy)

  const foot = document.createElement('div')
  foot.className = 'project-card__foot'
  const location = document.createElement('span')
  location.className = 'mini-meta'
  location.textContent = project.location || 'República Dominicana'
  const button = document.createElement('button')
  button.className = 'text-link'
  button.type = 'button'
  button.dataset.projectOpen = 'true'
  button.textContent = 'Ver proyecto'
  button.addEventListener('click', () => {
    const openProject = (
      window as typeof window & {
        openProject?: (
          title: string,
          description: string,
          image: string,
          location?: string,
          gallery?: string[],
          projectId?: string,
        ) => void
      }
    ).openProject
    openProject?.(
      project.name,
      project.description || 'Proyecto disponible',
      presentation.cover,
      project.location || undefined,
      presentation.gallery,
      project.id,
    )
  })

  foot.append(location, button)
  body.append(top, foot)
  article.append(media, body)
  return article
}

function populateProjectSelectors(projects: PublicProject[]) {
  document
    .querySelectorAll<HTMLSelectElement>('[data-public-project-select]')
    .forEach(select => {
      const previousValue = select.value
      const allLabel = select.dataset.allLabel || 'Seleccionar proyecto'
      const allValue =
        select.id === 'projectSelect'
        || select.id === 'inventoryProject'
        || select.id === 'publicInventoryProject'
          ? 'all'
          : ''

      select.replaceChildren()
      const first = document.createElement('option')
      first.value = allValue
      first.textContent = allLabel
      select.append(first)

      projects.forEach(project => {
        const option = document.createElement('option')
        option.value = project.name
        option.textContent = project.name
        option.dataset.projectId = project.id
        select.append(option)
      })

      const hasPrevious = [...select.options].some(
        option => option.value === previousValue,
      )
      select.value = hasPrevious ? previousValue : allValue
    })
}

function renderProjectCatalog(projects: PublicProject[], siteContent?: SiteContent) {
  const grids = document.querySelectorAll<HTMLElement>('[data-public-projects]')
  grids.forEach(grid => {
    grid.replaceChildren()
    if (!projects.length) {
      const empty = document.createElement('div')
      empty.className = 'cms-data-status cms-data-status--info'
      empty.textContent = 'No hay proyectos publicados en este momento.'
      grid.append(empty)
      return
    }

    projects.forEach(project => grid.append(renderProjectCard(project, siteContent)))
  })

  const eyebrow = document.getElementById('publishedProjectsEyebrow')
  if (eyebrow) eyebrow.textContent = `Proyectos ${activeTenant.tradeName}`

  const heading = document.getElementById('publishedProjectsHeading')
  if (heading) {
    heading.textContent =
      projects.length === 1
        ? '1 proyecto publicado.'
        : `${projects.length} proyectos publicados.`
  }
}

function selectorLimit() {
  return window.matchMedia('(max-width: 700px)').matches ? 3 : 5
}

type UnitFilters = {
  project: string
  propertyType: string
  status: string
  currency: string
  bedrooms: string
}

function matchesFilters(unit: PublicProjectUnit, filters: UnitFilters) {
  const projectMatches =
    !filters.project || filters.project === 'all' || unit.projectName === filters.project
  const typeMatches =
    !filters.propertyType || filters.propertyType === 'all' || unit.propertyType === filters.propertyType
  const statusMatches =
    !filters.status || filters.status === 'all' || unit.status === filters.status
  const currencyMatches =
    !filters.currency || filters.currency === 'all' || unit.currency === filters.currency
  const bedroomsMatches =
    !filters.bedrooms
    || filters.bedrooms === 'all'
    || (filters.bedrooms === '4'
      ? (unit.bedrooms ?? 0) >= 4
      : String(unit.bedrooms ?? '') === filters.bedrooms)

  return projectMatches && typeMatches && statusMatches && currencyMatches && bedroomsMatches
}

function publicSelectorFilters(): UnitFilters {
  const value = (id: string, fallback = 'all') =>
    (document.getElementById(id) as HTMLSelectElement | null)?.value || fallback

  return {
    project: value('publicInventoryProject'),
    propertyType: value('publicInventoryType'),
    status: value('publicInventoryStatus', 'available'),
    currency: value('publicInventoryCurrency'),
    bedrooms: 'all',
  }
}

function availabilityHref(filters: UnitFilters) {
  const params = new URLSearchParams()
  const existing = new URLSearchParams(window.location.search)
  const tenant = existing.get('tenant')
  if (tenant) params.set('tenant', tenant)

  if (filters.project && filters.project !== 'all') params.set('project', filters.project)
  if (filters.propertyType && filters.propertyType !== 'all') params.set('type', filters.propertyType)
  if (filters.status && filters.status !== 'all') params.set('status', filters.status)
  if (filters.currency && filters.currency !== 'all') params.set('currency', filters.currency)
  if (filters.bedrooms && filters.bedrooms !== 'all') params.set('bedrooms', filters.bedrooms)

  const query = params.toString()
  return query ? `/disponibilidad?${query}` : '/disponibilidad'
}

function renderSelectorUnits(
  grid: HTMLElement,
  units: PublicProjectUnit[],
  siteContent?: SiteContent,
) {
  const filters = publicSelectorFilters()
  const filtered = units.filter(unit => matchesFilters(unit, filters))
  grid.replaceChildren()

  if (!filtered.length) {
    const empty = document.createElement('div')
    empty.className = 'cms-data-status cms-data-status--info'
    empty.textContent = 'No hay propiedades que coincidan con los filtros seleccionados.'
    grid.append(empty)
  } else {
    const limit = selectorLimit()
    filtered
      .slice(0, limit)
      .forEach(unit => grid.append(renderPublicCard(unit, siteContent)))
  }

  const existing = grid.parentElement?.querySelector<HTMLElement>('[data-more-units-cta]')
  existing?.remove()

  const hiddenCount = Math.max(0, filtered.length - selectorLimit())
  if (hiddenCount > 0) {
    const wrap = document.createElement('div')
    wrap.className = 'availability-more'
    wrap.dataset.moreUnitsCta = 'true'

    const link = document.createElement('a')
    link.className = 'availability-more__link'
    link.href = availabilityHref(filters)
    link.setAttribute('aria-label', `Ver ${hiddenCount} propiedades adicionales`)
    link.innerHTML = `Ver ${hiddenCount} más <span aria-hidden="true">→</span>`

    wrap.append(link)
    grid.insertAdjacentElement('afterend', wrap)
  }
}

function bindPublicSelectorFilters(
  grid: HTMLElement,
  units: PublicProjectUnit[],
  siteContent?: SiteContent,
) {
  const ids = [
    'publicInventoryProject',
    'publicInventoryType',
    'publicInventoryStatus',
    'publicInventoryCurrency',
  ]

  ids.forEach(id => {
    const control = document.getElementById(id) as HTMLSelectElement | null
    if (!control || control.dataset.filterBound === 'true') return
    control.dataset.filterBound = 'true'
    control.addEventListener('change', () => renderSelectorUnits(grid, units, siteContent))
  })
}

function fullInventoryFilters(): UnitFilters {
  const value = (id: string, fallback = 'all') =>
    (document.getElementById(id) as HTMLSelectElement | null)?.value || fallback

  return {
    project: value('inventoryProject'),
    propertyType: value('inventoryType'),
    status: value('inventoryStatus', 'available'),
    currency: value('inventoryCurrency'),
    bedrooms: value('inventoryBedrooms'),
  }
}

function applyFullInventoryFilters() {
  const filters = fullInventoryFilters()
  document.querySelectorAll<HTMLElement>('#view-availability [data-public-units] .unit-card')
    .forEach(card => {
      const bedrooms = Number(card.dataset.bedrooms || 0)
      const visible =
        (!filters.project || filters.project === 'all' || card.dataset.project === filters.project)
        && (!filters.propertyType || filters.propertyType === 'all' || card.dataset.propertyType === filters.propertyType)
        && (!filters.status || filters.status === 'all' || card.dataset.status === filters.status)
        && (!filters.currency || filters.currency === 'all' || card.dataset.currency === filters.currency)
        && (!filters.bedrooms || filters.bedrooms === 'all'
          || (filters.bedrooms === '4'
            ? bedrooms >= 4
            : String(bedrooms) === filters.bedrooms))

      card.hidden = !visible
    })
}

function applyFiltersFromRoute() {
  if (window.location.pathname.replace(/\/+$/, '') !== '/disponibilidad') return

  const params = new URLSearchParams(window.location.search)
  const mappings = [
    ['project', 'inventoryProject'],
    ['type', 'inventoryType'],
    ['status', 'inventoryStatus'],
    ['currency', 'inventoryCurrency'],
    ['bedrooms', 'inventoryBedrooms'],
  ] as const

  mappings.forEach(([param, id]) => {
    const requested = params.get(param)
    if (!requested) return
    const select = document.getElementById(id) as HTMLSelectElement | null
    if (!select) return
    const exists = [...select.options].some(option => option.value === requested)
    if (exists) select.value = requested
  })

  applyFullInventoryFilters()
}

function bindFullInventoryFilters() {
  const ids = [
    'inventoryProject',
    'inventoryType',
    'inventoryStatus',
    'inventoryCurrency',
    'inventoryBedrooms',
  ]

  ids.forEach(id => {
    const control = document.getElementById(id) as HTMLSelectElement | null
    if (!control || control.dataset.filterBound === 'true') return
    control.dataset.filterBound = 'true'
    control.addEventListener('change', applyFullInventoryFilters)
  })
}

export function hydratePublicAvailability(
  snapshot: PublicAvailabilitySnapshot,
  siteContent?: SiteContent,
) {
  const grids = document.querySelectorAll<HTMLElement>('[data-public-units]')

  if (snapshot.state !== 'ready') {
    grids.forEach(grid => {
      grid.replaceChildren()
      const message = document.createElement('div')
      message.className = 'cms-data-status cms-data-status--warning'
      message.textContent = snapshot.message
      grid.append(message)
    })
    return
  }

  populateProjectSelectors(snapshot.projects)
  renderProjectCatalog(snapshot.projects, siteContent)

  grids.forEach(grid => {
    grid.replaceChildren()
    if (!snapshot.units.length) {
      const empty = document.createElement('div')
      empty.className = 'cms-data-status cms-data-status--info'
      empty.textContent = snapshot.projects.length
        ? 'Los proyectos están publicados, pero no hay propiedades públicas disponibles en este momento.'
        : 'No hay propiedades publicadas en este momento.'
      grid.append(empty)
      return
    }

    if (grid.classList.contains('connected-availability-grid')) {
      renderSelectorUnits(grid, snapshot.units, siteContent)
      bindPublicSelectorFilters(grid, snapshot.units, siteContent)
      return
    }

    snapshot.units.forEach(unit => grid.append(renderPublicCard(unit, siteContent)))
  })

  const selectorGrid = document.querySelector<HTMLElement>('.connected-availability-grid')
  if (selectorGrid && selectorGrid.dataset.responsiveBound !== 'true') {
    selectorGrid.dataset.responsiveBound = 'true'
    const media = window.matchMedia('(max-width: 700px)')
    const rerender = () => renderSelectorUnits(selectorGrid, snapshot.units, siteContent)
    media.addEventListener('change', rerender)
  }

  bindFullInventoryFilters()
  applyFiltersFromRoute()

  const status = document.getElementById('publicDataStatusText')
  if (status) {
    status.textContent =
      `Portal actualizado · ${snapshot.projects.length} proyecto${snapshot.projects.length === 1 ? '' : 's'} · ${snapshot.units.length} propiedad${snapshot.units.length === 1 ? '' : 'es'} publicada${snapshot.units.length === 1 ? '' : 's'}`
  }

  document
    .querySelectorAll<HTMLElement>('[data-availability-badge]')
    .forEach(badge => {
      badge.className = 'lozenge lozenge--success'
      badge.textContent = 'Disponibilidad actualizada'
    })

  ;(window as typeof window & { lucide?: { createIcons?: (options?: unknown) => void } }).lucide?.createIcons?.()
  window.dispatchEvent(new CustomEvent('webapp:availability-hydrated'))
}
